import { ChangeEvent } from 'react';
import { WrappedFieldProps, Field } from 'redux-form';
import cn from 'classnames';
import { Button } from 'components/Button';
import { Option, LabeledSelect } from 'components/Select';
import { DeliveryOptionItem } from 'models/DeliveryOptionItem';
import { rm, NO_ELEMENT } from 'utils';
import { Checkbox } from 'components/Shared/Form';
import { useToggle } from 'hooks';
import styles from './servicePointDeliveryOptions.scss';

interface OwnProps {
  deliveryOptions: DeliveryOptionItem[];
  deliveryId: string;
  disabled?: boolean;
  simpleMode: boolean;
  toggleSimpleMode: () => void;
  selectedServicePoint?: DeliveryOptionItem;
  onChange?: (
    deliveryId: string,
    deliveryOption: DeliveryOptionItem,
    syncServicePoints: boolean
  ) => void;
  hasRelatedDeliveries: boolean;
}

export const ServicePointDeliveryOptions = ({
  deliveryId,
  deliveryOptions,
  selectedServicePoint,
  disabled,
  onChange,
  hasRelatedDeliveries
}: OwnProps) => {
  const [useSameServicePoint, toggleUseSameServicePoint] = useToggle(true);

  const handleOnChange = (_e: ChangeEvent, deliveryOptionIdentifier: string) => {
    const deliveryOption = deliveryOptions.find(
      o => o.deliveryOptionIdentifier === deliveryOptionIdentifier
    );
    if (onChange && deliveryOption) {
      onChange(deliveryId, deliveryOption, useSameServicePoint);
    }
  };

  if (!selectedServicePoint || !selectedServicePoint.servicePointInformation) {
    return NO_ELEMENT;
  }

  return (
    <div className={styles.servicePointDeliveryOptions}>
      <div className={styles.servicePointInfo}>
        <div className={styles.selectedServicePointInfo}>
          <div>{selectedServicePoint.servicePointInformation.streetName}</div>
          <div>
            {selectedServicePoint.servicePointInformation.postCode}
            &nbsp;
            {selectedServicePoint.servicePointInformation.city}
          </div>
        </div>
        <Field
          selectedId={selectedServicePoint.deliveryOptionIdentifier}
          deliveryOptions={deliveryOptions}
          disabled={disabled}
          name={'selected'}
          onChange={handleOnChange}
          component={renderServicePointsSelect}
        />
        {hasRelatedDeliveries && (
          <Checkbox
            className={styles.syncServicePointCheckbox}
            appearance="secondary"
            color="charcoal"
            checked={useSameServicePoint}
            onChange={toggleUseSameServicePoint}
          >
            {rm('checkout_sync_service_points')}
          </Checkbox>
        )}
      </div>
    </div>
  );
};

function renderServicePointsSelect({
  input,
  deliveryOptions,
  disabled,
  selectedId
}: WrappedFieldProps & {
  selectedId?: string;
  deliveryOptions: DeliveryOptionItem[];
  disabled?: boolean;
}) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const selectedValue =
    deliveryOptions.find(o => o.deliveryOptionIdentifier === selectedId)
      ?.deliveryOptionIdentifier ||
    input.value ||
    selectedId;

  return (
    <>
      <p className={styles.selectServicePointLabel}>{rm('checkout_service_point_change')}</p>
      <LabeledSelect
        label={rm('checkout_service_point')}
        isSelected={!!input.value || !!selectedId}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        selectedValue={selectedValue}
        onChange={input.onChange}
        disabled={disabled}
        scrollable
      >
        {deliveryOptions.map(o => (
          <Option
            key={o.deliveryOptionIdentifier}
            value={o.deliveryOptionIdentifier}
            label={o.servicePointInformation ? o.servicePointInformation.displayName : ''}
          />
        ))}
      </LabeledSelect>
    </>
  );
}

interface ServicePointDeliveryButtonProps {
  className?: string;
  selectedServicePoint?: DeliveryOptionItem;
  hasMultipleDeliveryOptions: boolean;
  onClick: () => void;
  disabled?: boolean;
}
export const ServicePointDeliveryButton = ({
  className,
  selectedServicePoint,
  hasMultipleDeliveryOptions,
  onClick,
  disabled
}: ServicePointDeliveryButtonProps) => (
  <div className={cn(styles.servicePointDeliveryButton, className)}>
    {selectedServicePoint && !!selectedServicePoint.servicePointInformation && (
      <div>
        {rm('checkout_delivery_sent_to')}
        &nbsp;
        <span className={styles.servicePointName}>
          {selectedServicePoint.servicePointInformation.displayName}
        </span>
      </div>
    )}
    {hasMultipleDeliveryOptions && (
      <Button
        className={styles.changeDeliveryOption}
        appearance="tertiary"
        onClick={onClick}
        disabled={disabled}
      >
        {rm('checkout_change_delivery_drop_point_rd3')}
      </Button>
    )}
  </div>
);
